import React from 'react';
import '../css/privacypolicy.css';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  return (
    <div className="policy-page-wrapper">
      <Helmet>
        <title>Privacy Policy | How We Protect Your Data at PolicySamjho</title>
        <meta
          name="description"
          content="Learn how PolicySamjho protects your personal and insurance data. Our privacy policy explains data collection, usage, security practices, and your rights clearly."
        />
        <meta name="keywords" content="privacy policy, data protection, insurance data security, Policy Samjho privacy, user data rights, personal information security" />
        <link
          rel="canonical"
          href="https://policysamjho.com/privacy-policy/"
        />
        <meta name="robots" content="index, follow" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Privacy Policy",
            "url": "https://policysamjho.com/privacy-policy/",
            "description":
              "Privacy policy of PolicySamjho explaining how user data is collected, used, stored, and protected.",
            "publisher": {
              "@type": "Organization",
              "name": "Policy Samjho",
              "url": "https://policysamjho.com/",
              "logo": {
                "@type": "ImageObject",
                "url":
                  "https://policysamjho.com/assets/frontend/img/logo.png"
              }
            }
          })}
        </script>
      </Helmet>
      <div className="privacy-policy-container">
        <h1 className="policy-main-title">Privacy Policy</h1>

        <section className="policy-section">
          <h2>1. Introduction</h2>
          <p>
            Policy Samjho IMF Private Limited (referred to as "Policy Samjho," "we," "us," or "our") operates
            <a href="https://policysamjho.com/"> https://policysamjho.com/ </a>
            (the "Website" or "Platform"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our Website or use our services.
          </p>
          <p>
            By accessing or using our Platform, you agree to this Privacy Policy. If you disagree with any part of this policy, please discontinue use of our services immediately.
          </p>
        </section>

        <section className="policy-section">
          <h2>2. Information We Collect</h2>
          <h3>2.1 Personal Information</h3>
          <p>We may collect the following personal information when you interact with our services:</p>
          <ul>
            <li>Full name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Age and date of birth</li>
            <li>Location information</li>
            <li>Financial information (income, existing insurance policies)</li>
            <li>Health-related information (for insurance advisory purposes)</li>
          </ul>
          <h3>2.2 Automatically Collected Information</h3>
          <p>When you access our Platform, we automatically collect certain technical information:</p>
          <ul>
            <li>IP address and device identifiers</li>
            <li>Browser type and version</li>
            <li>Operating system</li>
            <li>Pages visited and time spent on each page</li>
            <li>Referring website addresses</li>
            <li>Session duration and interaction patterns</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use the collected information for the following purposes:</p>
          <ul>
            <li>To provide insurance advisory and comparison services</li>
            <li>To create and manage user accounts</li>
            <li>To communicate with you via email, phone, SMS, or WhatsApp</li>
            <li>To send service updates, newsletters, and promotional materials</li>
            <li>To process insurance applications and facilitate policy purchases</li>
            <li>To improve our Website and service offerings</li>
            <li>To analyze user behavior and optimize user experience</li>
            <li>To comply with legal and regulatory obligations</li>
            <li>To prevent fraud and ensure platform security</li>
            <li>To resolve disputes and enforce our Terms of Service</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>4. Information Sharing and Disclosure</h2>
          <p>We may share your information with the following parties:</p>
          <h3>4.1 Insurance Providers</h3>
          <p>When you choose to purchase an insurance policy, we share relevant information with the insurance company to process your application. This may include personal, financial, and health information necessary for underwriting.</p>
          <h3>4.2 Regulatory Authorities</h3>
          <p>We may share information with the Insurance Regulatory and Development Authority of India (IRDAI) or other government bodies as required by law or regulation.</p>
          <h3>4.3 Service Providers</h3>
          <p>We work with third-party service providers for analytics (Google Analytics), email services (Mailchimp), payment processing, and communication platforms. These providers have limited access to your information solely to perform services on our behalf.</p>
          <h3>4.4 Legal Requirements</h3>
          <p>We may disclose your information if required by law, court order, or governmental request, or to protect our legal rights, prevent fraud, or ensure user safety.</p>
        </section>

        <section className="policy-section">
          <h2>5. Cookies and Tracking Technologies</h2>
          <p>Our Website uses cookies and similar technologies to enhance your browsing experience. Cookies are small text files stored on your device that help us remember your preferences and understand how you use our Platform.</p>
          <p>Types of cookies we use:</p>
          <ul>
            <li>Essential cookies: Required for basic website functionality</li>
            <li>Analytics cookies: Help us understand user behavior and improve our services</li>
            <li>Functional cookies: Remember your preferences and settings</li>
            <li>Marketing cookies: Used to deliver relevant advertisements</li>
          </ul>
          <p>You can control cookie settings through your browser preferences. However, disabling cookies may limit certain features of our Platform.</p>
        </section>

        <section className="policy-section">
          <h2>6. Data Security</h2>
          <p>We implement appropriate technical and organizational security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, access controls, and regular security assessments.</p>
          <p>However, no method of internet transmission or electronic storage is completely secure. While we strive to protect your information, we cannot guarantee absolute security.</p>
        </section>

        <section className="policy-section">
          <h2>7. Data Retention</h2>
          <p>We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. When information is no longer needed, we will securely delete or anonymize it.</p>
          <p>For regulatory compliance purposes (IRDAI requirements, tax laws, etc.), certain information may be retained for extended periods as mandated by applicable regulations.</p>
        </section>

        <section className="policy-section">
          <h2>8. Your Rights and Choices</h2>
          <p>Under applicable data protection laws, you have the following rights:</p>
          <ul>
            <li>Access: Request copies of your personal information</li>
            <li>Correction: Request correction of inaccurate or incomplete information</li>
            <li>Deletion: Request deletion of your personal information (subject to legal obligations)</li>
            <li>Restriction: Request limitation on how we process your information</li>
            <li>Objection: Object to processing of your information for certain purposes</li>
            <li>Portability: Request transfer of your information to another service provider</li>
            <li>Withdraw Consent: Withdraw previously given consent for data processing</li>
          </ul>
          <p>To exercise these rights, contact us at <strong>connect@policysamjho.com</strong>. We will respond to your request within 30 days.</p>
        </section>

        <section className="policy-section">
          <h2>9. Third-Party Links</h2>
          <p>Our Platform may contain links to external websites or services not operated by us. We are not responsible for the privacy practices or content of these third-party sites. We encourage you to review their privacy policies before providing any personal information.</p>
        </section>

        <section className="policy-section">
          <h2>10. Children's Privacy</h2>
          <p>Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a minor, please contact us immediately, and we will take steps to delete such information.</p>
        </section>

        <section className="policy-section">
          <h2>11. International Data Transfers</h2>
          <p>Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. By using our services, you consent to such transfers. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.</p>
        </section>

        <section className="policy-section">
          <h2>12. Data Breach Notification</h2>
          <p>In the event of a data breach that may compromise your personal information, we will notify affected users and relevant authorities as required by law. If you suspect unauthorized access to your information, please contact us immediately at <strong>connect@policysamjho.com</strong>.</p>
        </section>

        <section className="policy-section">
          <h2>13. Marketing Communications</h2>
          <p>We may send you promotional emails, SMS, or WhatsApp messages about our services, special offers, or insurance-related content. You can opt out of marketing communications at any time by clicking the unsubscribe link in emails or contacting us directly. Please note that you will still receive essential service-related communications.</p>
        </section>

        <section className="policy-section">
          <h2>14. Changes to This Privacy Policy</h2>
          <p>We reserve the right to update this Privacy Policy at any time. Changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically. Continued use of our services after changes constitutes acceptance of the updated policy.</p>
        </section>

        <section className="policy-section">
          <h2>15. Contact Information</h2>
          <p>For questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact us:</p>
          <p>
            <strong>Policy Samjho IMF Private Limited</strong><br />
            Email: connect@policysamjho.com<br />
            Website: https://policysamjho.com/<br />
            Phone: 9755531167, 9584484800
          </p>
        </section>

        <section className="policy-section">
          <h2>16. Grievance Officer</h2>
          <p>In accordance with the Information Technology Act, 2000, and the Consumer Protection (E-Commerce) Rules, 2020, we have appointed a Grievance Officer to address your concerns:</p>
          <p>
            <strong>Name:</strong> Om Prakash Mishra<br />
            Email: op@policysamjho.com<br />
            Address: LG-1, Rudraksh Apartment, 16 Meerapath, Indore (MP) 452001<br />
            Response Time: We aim to acknowledge grievances within 48 hours and resolve them within 30 days.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;